import {
  InitialTableState,
  SortingState,
  TableOptions,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { useSyncCenterSettings } from 'src/hooks/api/useSyncCenterSetting';
import { NoData, Table, TableProps } from 'src/tables/Table';
import { MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { generateShimmeringRows } from 'src/utils/dataTableUtils';
import { SomethingWentWrong } from 'src/views';

import { usePaginationSettings } from '../Table/hooks/usePaginationSettings';
import { AdCampaignEntityWithMetrics } from './AdCampaignsTable.type';
import { useGetAdCampaignsTableColumns } from './useGetAdCampaignsTableColumns';

export type AdCampaignsTableDataProps = {
  adCampaignEntities?: AdCampaignEntityWithMetrics[] | null;
  failedToRetrieveData: boolean;
  disablePagination?: boolean;
  useVirtuoso?: boolean;
};

type AdCampaignsTableProps = AdCampaignsTableDataProps & {
  failedToRetrieveData: boolean;
  onMount?: () => void;
  onUnmount?: (state: object) => void;
  initState?: InitialTableState;
  isItemsLoading?: boolean;
} & Pick<
    TableProps<AdCampaignEntityWithMetrics | null>,
    'RowComponent' | 'virtuosoItemSize'
  >;

const PAGE_SIZE = 10;

const getRowId = (
  originalRow: AdCampaignEntityWithMetrics | null,
  index: number
) => (originalRow?.campaignId ?? index).toString();

export function AdCampaignsTable({
  adCampaignEntities,
  failedToRetrieveData,
  onMount,
  onUnmount,
  initState,
  disablePagination,
  isItemsLoading,
  RowComponent,
  virtuosoItemSize,
}: AdCampaignsTableProps) {
  const settings = useSyncCenterSettings();

  const adCampaignsText = useContent(ContentId.AdCampaigns);

  const [sorting, setSorting] = useState<SortingState>(
    initState?.sorting || [{ id: 'name', desc: true }]
  );

  const { pagination, setPagination } = usePaginationSettings(
    adCampaignEntities?.length ?? 0,
    -1,
    PAGE_SIZE,
    disablePagination,
    initState
  );

  useEffect(() => {
    onMount?.();
  });

  useEffect(() => {
    return () => onUnmount?.({ pagination, sorting });
  }, [pagination, sorting, onUnmount]);

  const { data, hasNoDataLoad } = useMemo(() => {
    const data = adCampaignEntities?.length
      ? adCampaignEntities
      : adCampaignEntities == null || isItemsLoading
        ? (generateShimmeringRows(0) as null[])
        : adCampaignEntities;

    const hasNoDataLoad =
      adCampaignEntities == null
        ? true
        : adCampaignEntities.length > MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS;

    return { data, hasNoDataLoad };
  }, [isItemsLoading, adCampaignEntities]);

  const columns = useGetAdCampaignsTableColumns();

  const options = useMemo<
    Partial<TableOptions<AdCampaignEntityWithMetrics | null>>
  >(
    () => ({
      data,
      columns: columns,
      state: {
        pagination,
        sorting,
      },
      enableRowSelection: false,
      enableMultiRowSelection: false,
      enableSubRowSelection: false,
      getRowId: getRowId,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
    }),
    [data, columns, pagination, sorting, setPagination]
  );

  return failedToRetrieveData ? (
    <SomethingWentWrong
      message={<Content id={ContentId.FailToLoadListContent} />}
    />
  ) : hasNoDataLoad ? (
    <NoData>
      <WarningMessage
        message={
          <FormatContent
            id={FormatContentId.ApplyFilterToReduceCountsToSeeData}
            params={[MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS.toString()]}
          />
        }
      />
    </NoData>
  ) : data?.length > 0 ? (
    <Table
      options={options}
      tableLayout="fixed"
      withOuterPadding
      useVirtuoso
      useStickyHeader={disablePagination}
      RowComponent={RowComponent}
      virtuosoItemSize={virtuosoItemSize}
    />
  ) : (
    <NoData>
      <FormatContent
        id={FormatContentId.NoDataAvailable}
        params={adCampaignsText}
      />
    </NoData>
  );
}
